/** Supplier Onboarding review queue (Demo Mode only, local-only - no backend persistence
 * yet). Tabbed request list (Supplier requests/Review queue/Approved/Rejected) with a
 * review side panel covering duplicate check, existing alternatives, required documents,
 * commercial/compliance review, approval decision and activation scope. Distinct from the
 * step-by-step onboarding wizard at `./index.tsx` (reached here via "New request"), which
 * remains the data-capture flow for a single supplier once a request is approved to
 * proceed. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, Drawer, MenuItem, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { Section } from '../../../../components/ui/Section';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import {
  OnboardingActivationScope,
  OnboardingRequestStatus,
  supplierOnboardingRequestsSeed,
  SupplierOnboardingRequest,
} from '../../../../mocks/data/supplierOnboardingRequests';

const TAB_STATUS: Record<number, OnboardingRequestStatus> = {
  0: 'Submitted',
  1: 'UnderReview',
  2: 'Approved',
  3: 'Rejected',
};

const TAB_LABELS: Record<number, (t: (key: string, fallback: string) => string) => string> = {
  0: (t) => t('supplierRequests', 'Supplier requests'),
  1: (t) => t('reviewQueue', 'Review queue'),
  2: (t) => t('approvedRequests', 'Approved requests'),
  3: (t) => t('rejectedRequests', 'Rejected requests'),
};

const URGENCY_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  High: 'error',
  Medium: 'warning',
  Low: 'default',
};

const ACTIVATION_SCOPE_LABEL: Record<OnboardingActivationScope, string> = {
  SiteOnly: 'Site only',
  SiteGroup: 'Site group',
  ClientAccount: 'Client account',
  AllSites: 'All sites',
};

const OnboardingQueuePage = () => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [requests, setRequests] = useState<SupplierOnboardingRequest[]>(supplierOnboardingRequestsSeed);
  const [tabIndex, setTabIndex] = useState(0);
  const [reviewing, setReviewing] = useState<SupplierOnboardingRequest | null>(null);
  const [activationScope, setActivationScope] = useState<OnboardingActivationScope>('SiteOnly');

  const rows = useMemo(
    () => requests.filter((request) => request.status === TAB_STATUS[tabIndex]),
    [requests, tabIndex],
  );

  const openReview = (request: SupplierOnboardingRequest) => {
    setReviewing(request);
    setActivationScope(request.activationScope ?? 'SiteOnly');
  };

  const handleMoveToReview = (id: number) => {
    setRequests((current) => current.map((request) => (request.id === id ? { ...request, status: 'UnderReview' } : request)));
    setReviewing(null);
  };

  const handleDecision = (id: number, status: 'Approved' | 'Rejected') => {
    setRequests((current) => current.map((request) => (request.id === id
      ? {
        ...request,
        status,
        activationScope: status === 'Approved' ? activationScope : request.activationScope,
        decisionNotes: status === 'Approved'
          ? t('approvedDecisionNote', 'Approved by Procurement.')
          : t('rejectedDecisionNote', 'Rejected by Procurement.'),
      }
      : request)));
    setReviewing(null);
  };

  return (
    <PageArea>
      <Stack direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}>
        <PageTitle>{t('onboardingQueueTitle', 'Supplier Onboarding')}</PageTitle>
        <Button variant="primary"
          size="lg"
          onClick={() => navigate('/procurement/suppliers/onboarding/create')}>
          {t('newOnboardingRequest', 'New onboarding request')}
        </Button>
      </Stack>

      <Tabs value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('supplierRequests', 'Supplier requests')} />
        <Tab label={t('reviewQueue', 'Review queue')} />
        <Tab label={t('approvedRequests', 'Approved requests')} />
        <Tab label={t('rejectedRequests', 'Rejected requests')} />
      </Tabs>

      <Section>
        <TableHeader>
          {TAB_LABELS[tabIndex](t)}
          <Pill text={t('requestCount', '{{count}} requests', { count: rows.length })} />
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('requestedSupplierName', 'Requested supplier name')}</TableCell>
                <TableCell>{t('requestedBy', 'Requested by')}</TableCell>
                <TableCell>{t('site', 'Site')}</TableCell>
                <TableCell>{t('clientAccount', 'Client account')}</TableCell>
                <TableCell>{t('productCategoryNeeded', 'Product/category needed')}</TableCell>
                <TableCell>{t('expectedUsage', 'Expected usage')}</TableCell>
                <TableCell>{t('urgency', 'Urgency')}</TableCell>
                <TableCell>{t('submittedDate', 'Submitted date')}</TableCell>
                <TableCell>{t('status', 'Status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9}>{t('noOnboardingRequests', 'No requests in this view.')}</TableCell></TableRow>
              )}
              {rows.map((request) => (
                <TableRow key={request.id}
                  hover
                  onClick={() => openReview(request)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell>{request.requestedSupplierName}</TableCell>
                  <TableCell>{request.requestedBy}</TableCell>
                  <TableCell>{request.site}</TableCell>
                  <TableCell>{request.clientAccount}</TableCell>
                  <TableCell>{request.productCategoryNeeded}</TableCell>
                  <TableCell>{request.expectedUsage}</TableCell>
                  <TableCell><Chip label={request.urgency}
                    size="small"
                    color={URGENCY_COLOR[request.urgency]} /></TableCell>
                  <TableCell>{request.submittedDate}</TableCell>
                  <TableCell>{request.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Drawer anchor="right"
        open={!!reviewing}
        onClose={() => setReviewing(null)}>
        {reviewing && (
          <Box sx={{ width: 440, p: 4 }}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 1 }}>{reviewing.requestedSupplierName}</Typography>
            <Typography variant="textXS"
              color="text.secondary"
              sx={{ mb: 4 }}>{reviewing.reason}</Typography>

            <Stack spacing={4}>
              <Box>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 1 }}>{t('duplicateSupplierCheck', 'Duplicate supplier check')}</Typography>
                <Typography variant="textSM">
                  {reviewing.duplicateSupplierName
                    ? t('duplicateFound', 'Possible duplicate: {{name}}', { name: reviewing.duplicateSupplierName })
                    : t('noDuplicateFound', 'No duplicate supplier found.')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 1 }}>{t('existingSupplierAlternatives', 'Existing supplier alternatives')}</Typography>
                {reviewing.existingAlternatives.length === 0 ? (
                  <Typography variant="textSM">{t('noAlternatives', 'No existing alternatives identified.')}</Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {reviewing.existingAlternatives.map((alternative) => (
                      <Typography key={alternative}
                        variant="textSM">{alternative}</Typography>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 1 }}>{t('requiredDocuments', 'Required documents')}</Typography>
                {reviewing.requiredDocuments.length === 0 ? (
                  <Typography variant="textSM">{t('noRequiredDocuments', 'None outstanding.')}</Typography>
                ) : (
                  <Stack direction="row"
                    spacing={1}
                    flexWrap="wrap">
                    {reviewing.requiredDocuments.map((document) => (
                      <Chip key={document}
                        label={document}
                        size="small" />
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 1 }}>{t('commercialReview', 'Commercial review')}</Typography>
                <Typography variant="textSM">{reviewing.commercialReviewNotes ?? t('notYetReviewed', 'Not yet reviewed.')}</Typography>
              </Box>

              <Box>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 1 }}>{t('complianceReview', 'Compliance review')}</Typography>
                <Typography variant="textSM">{reviewing.complianceReviewNotes ?? t('notYetReviewed', 'Not yet reviewed.')}</Typography>
              </Box>

              {reviewing.status !== 'Approved' && reviewing.status !== 'Rejected' && (
                <Box>
                  <Typography variant="textSM"
                    fontWeight={600}
                    sx={{ mb: 1 }}>{t('activationScope', 'Activation scope')}</Typography>
                  <TextField select
                    fullWidth
                    value={activationScope}
                    onChange={(e) => setActivationScope(e.target.value as OnboardingActivationScope)}
                  >
                    {(Object.keys(ACTIVATION_SCOPE_LABEL) as OnboardingActivationScope[]).map((scope) => (
                      <MenuItem key={scope}
                        value={scope}>{t(`activationScope${scope}`, ACTIVATION_SCOPE_LABEL[scope])}</MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              {(reviewing.status === 'Approved' || reviewing.status === 'Rejected') && reviewing.decisionNotes && (
                <Box>
                  <Typography variant="textSM"
                    fontWeight={600}
                    sx={{ mb: 1 }}>{t('approvalDecision', 'Approval decision')}</Typography>
                  <Chip label={reviewing.status}
                    color={reviewing.status === 'Approved' ? 'success' : 'error'}
                    size="small"
                    sx={{ mb: 1 }} />
                  <Typography variant="textSM">{reviewing.decisionNotes}</Typography>
                  {reviewing.activationScope && (
                    <Typography variant="textXS"
                      color="text.secondary">{t('activationScope', 'Activation scope')}: {t(`activationScope${reviewing.activationScope}`, ACTIVATION_SCOPE_LABEL[reviewing.activationScope])}</Typography>
                  )}
                </Box>
              )}

              {reviewing.status === 'Submitted' && (
                <Button variant="secondary"
                  onClick={() => handleMoveToReview(reviewing.id)}>
                  {t('moveToReviewQueue', 'Move to review queue')}
                </Button>
              )}

              {reviewing.status === 'UnderReview' && (
                <Stack direction="row"
                  spacing={2}>
                  <Button variant="primary"
                    onClick={() => handleDecision(reviewing.id, 'Approved')}>
                    {t('approveRequest', 'Approve')}
                  </Button>
                  <Button variant="secondary"
                    onClick={() => handleDecision(reviewing.id, 'Rejected')}>
                    {t('rejectRequest', 'Reject')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </Drawer>
    </PageArea>
  );
};

export default OnboardingQueuePage;
